import React from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  IconButton,
  Image,
  Link,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue as mode,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { MdVolumeUp } from "react-icons/md";

import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { Polaroid } from "#app/components/Polaroid.jsx";
import { RESERVATION_SYSTEM_COUNT } from "#app/config/countries.js";

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  return getSeoMeta({
    title: "About | Schnerp",
    description:
      "Schnerp started in 2022 after a friend laughed at me at a dinner party. Here's the story.",
    url: `${baseUrl}/about`,
    image: getDefaultOgImage(baseUrl),
  });
}

const HERO_SRC = "/about/lake-ohara-celebration.webp";
// 12px-wide blur of the hero, shown behind it while the full photo loads.
const HERO_PLACEHOLDER =
  "data:image/webp;base64,UklGRm4AAABXRUJQVlA4IGIAAAAwAgCdASoMABAAA4BaJbACdAEVjJJc6aKSgAD80S5vWRud2QTHDTvL5A40QuUPLRjVpc+ktwcNp8pRqD59fx1KZbTsSkqaQFWKPIS1q2kUcYjralYy5IoZJF33q+pjhzQAAA==";

// Start the hero download with the HTML rather than after hydration.
export function links() {
  return [
    { rel: "preload", as: "image", href: HERO_SRC, fetchpriority: "high" },
  ];
}

const waveAnimation = keyframes`
  0%   { transform: rotate(0deg); }
  10%  { transform: rotate(14deg); }
  20%  { transform: rotate(-8deg); }
  30%  { transform: rotate(14deg); }
  40%  { transform: rotate(-4deg); }
  50%  { transform: rotate(10deg); }
  60%  { transform: rotate(0deg); }
  100% { transform: rotate(0deg); }
`;

function Wave() {
  return (
    <Text
      as="span"
      display="inline-block"
      transformOrigin="70% 70%"
      animation={`${waveAnimation} 2.4s ease-in-out 0.4s 2`}
      aria-hidden="true"
    >
      👋
    </Text>
  );
}

function SquiggleUnderline({ color }) {
  return (
    <Box
      as="svg"
      viewBox="0 0 240 12"
      width="100%"
      height="10px"
      display="block"
      mt={1}
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <path
        d="M2 6 Q 30 0, 60 6 T 120 6 T 180 6 T 238 6"
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </Box>
  );
}

const PRONUNCIATION_CLIPS = ["eira.m4a", "justin.m4a", "dan.m4a"];

function SchnerpPronounce({ color }) {
  const [audios, setAudios] = React.useState([]);
  const [audioIndex, setAudioIndex] = React.useState(0);

  React.useEffect(() => {
    setAudios(PRONUNCIATION_CLIPS.map((clip) => new Audio("/audio/" + clip)));
  }, []);

  const playAudio = () => {
    if (!audios.length) return;
    audios[audioIndex].play();
    setAudioIndex((audioIndex + 1) % audios.length);
  };

  return (
    <Box as="span" display="inline-flex" alignItems="center" gap={2}>
      <Box
        as="button"
        type="button"
        onClick={playAudio}
        color={color}
        _hover={{ textDecoration: "underline" }}
        cursor="pointer"
      >
        Schnerp.
      </Box>
      <IconButton
        aria-label="Hear how to say Schnerp"
        variant="ghost"
        size="sm"
        isRound
        color={color}
        icon={<MdVolumeUp fontSize="20px" />}
        onClick={playAudio}
      />
    </Box>
  );
}

function SectionHeading({ children, accent }) {
  return (
    <Box mt={12} mb={4}>
      <Heading as="h2" size="md" letterSpacing="tight" display="inline-block">
        {children}
      </Heading>
      <Box mt={2} h="3px" w="48px" bg={accent} borderRadius="full" />
    </Box>
  );
}

function PullQuote({ children, accent }) {
  const bg = mode("purple.50", "whiteAlpha.100");
  return (
    <Box
      position="relative"
      bg={bg}
      borderRadius="2xl"
      px={{ base: 6, md: 10 }}
      py={{ base: 6, md: 8 }}
      my={8}
      overflow="hidden"
    >
      <Text
        as="span"
        position="absolute"
        top="-30px"
        left={4}
        fontSize="140px"
        lineHeight="1"
        fontFamily="serif"
        color={accent}
        opacity={0.25}
        aria-hidden="true"
      >
        &ldquo;
      </Text>
      <Box position="relative">{children}</Box>
    </Box>
  );
}

const GRID_PHOTOS = [
  {
    src: "/about/floe-lake-campground.webp",
    alt: "Floe Lake campground, Kootenay National Park",
    caption: "Backpacking to Floe Lake, Kootenay.",
    rotate: "-2deg",
  },
  {
    src: "/about/maligne-lake-campground.webp",
    alt: "Maligne Lake backcountry campground, Jasper",
    caption: "Paddling on Maligne Lake, Jasper.",
    rotate: "1.8deg",
  },
  {
    src: "/about/tombstone-coffee-break.webp",
    alt: "Coffee break bikepacking to Tombstone, Kananaskis",
    caption: "Bikepacking in Kananaskis.",
    rotate: "-2.2deg",
  },
  {
    src: "/about/conto-wa.jpg",
    alt: "Jimny and rooftop tent at Conto campground, Western Australia",
    caption: "Road tripping in Western Australia.",
    rotate: "1.5deg",
  },
];

export default function AboutPage() {
  const accent = mode("purple.500", "purple.300");
  const mutedColor = mode("gray.600", "gray.400");
  const dialogueColor = mode("gray.700", "gray.100");
  const heroBgBlob = mode("purple.100", "whiteAlpha.100");
  const quoteColor = mode("gray.800", "gray.100");

  const bodyFont = { base: "md", md: "lg" };

  return (
    <Container maxW="5xl" px={{ base: 6, md: 8 }} py={{ base: 6, md: 10 }}>
      {/* Hero */}
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        gap={{ base: 8, md: 12 }}
        mb={{ base: 10, md: 14 }}
      >
        <Box
          flex={{ base: "none", md: "0 0 42%" }}
          position="relative"
          alignSelf={{ base: "center", md: "auto" }}
          maxW={{ base: "sm", md: "none" }}
          w="100%"
        >
          <Box
            position="absolute"
            inset="-16px"
            bg={heroBgBlob}
            borderRadius="50% 40% 60% 50% / 50% 60% 40% 50%"
            zIndex={0}
            aria-hidden="true"
          />
          <Box position="relative" zIndex={1}>
            <Polaroid
              src={HERO_SRC}
              placeholder={HERO_PLACEHOLDER}
              priority
              alt="Daniel at Lake O'Hara in summer 2022, the trip that started Schnerp"
              caption="Lake O'Hara, summer 2022. The spot that started it all."
              rotate="-2.5deg"
              ratio="4/5"
            />
          </Box>
        </Box>

        <Box flex="1" minW={0}>
          <Heading
            as="h1"
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            lineHeight="1.1"
            letterSpacing="tight"
            fontWeight="extrabold"
          >
            Hey <Wave />, I&rsquo;m{" "}
            <Box
              as="span"
              display="inline-block"
              position="relative"
              color={accent}
            >
              Daniel.
              <Box
                position="absolute"
                left={0}
                right={0}
                bottom="-6px"
                aria-hidden="true"
              >
                <SquiggleUnderline color={accent} />
              </Box>
            </Box>
            <br />I made this thing.
          </Heading>

          <Text fontSize={bodyFont} mt={6} lineHeight="1.65">
            Thanks for being here. Really. I built Schnerp on my laptop one
            weekend in 2022, fully expecting nobody but me to use it.
          </Text>
        </Box>
      </Flex>

      <Box maxW="3xl" mx="auto">
        <SectionHeading accent={accent}>How it started</SectionHeading>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7">
          <Text>Schnerp exists because I got jealous at a dinner party.</Text>
          <Text>
            It was March 2022, and a friend had just booked Lake O&rsquo;Hara
            &mdash; a gorgeous high-alpine lake in the Canadian Rockies. He was
            telling everyone. I wanted to go too, so I asked:
          </Text>

          <Stack
            spacing={3}
            pl={{ base: 4, md: 6 }}
            borderLeftWidth="3px"
            borderLeftColor={accent}
            py={2}
            fontSize={bodyFont}
          >
            <Text fontStyle="italic" color={dialogueColor}>
              &ldquo;Nice &mdash; how do I get a spot?&rdquo;
            </Text>
            <Text>He laughed.</Text>
            <Text fontStyle="italic" color={dialogueColor}>
              &ldquo;You don&rsquo;t. Bookings dropped back in January. I was
              60,000th in the queue and everything was sold out by the time I
              got in.&rdquo;
            </Text>
            <Text fontStyle="italic" color={dialogueColor}>
              &ldquo;Wait, you just said you booked one.&rdquo;
            </Text>
            <Text fontStyle="italic" color={dialogueColor}>
              &ldquo;I refreshed the page all day at work until someone
              cancelled.&rdquo;
            </Text>
          </Stack>

          <Text>
            Wait 💡 &mdash; refreshing a page all day is the wrong job for a
            person, and the perfect job for a computer.
          </Text>
          <Text>
            So I went home and wrote a tiny script that watched the Parks Canada
            page and emailed me when something opened up. A few weeks later, a
            cancellation hit and I booked it.
          </Text>
          <Text>That summer I made it to Lake O&rsquo;Hara.</Text>
        </Stack>

        <Box my={10}>
          <Polaroid
            src="/about/lake-ohara-hike.webp"
            alt="Hiking the Lake O'Hara alpine circuit"
            caption="On the alpine circuit, the pictures take themselves really."
            rotate="1.5deg"
            ratio="3/2"
            maxW="md"
            mx="auto"
          />
        </Box>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7">
          <Text>
            That should&rsquo;ve been the end of the story. I&rsquo;d built
            myself a toy, it worked once, on to the next thing.
          </Text>
          <Text>
            But I told a friend about it. He asked if I could set one up for
            him. Then he told his friends. They told theirs. My inbox started
            filling up with strangers asking if I could watch a campground for
            them too. Somewhere along the way it stopped being a favour and
            became the thing I do.
          </Text>
        </Stack>

        <SectionHeading accent={accent}>
          So, uh, why &ldquo;Schnerp&rdquo;?
        </SectionHeading>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7">
          <Text>Valid question.</Text>
          <Text>
            It&rsquo;s a word my friend group uses when talking about hunting
            powder at a ski resort a few days after a storm. We&rsquo;ll put in
            massive effort, staying high on traverses and suffering long
            run-outs, just to{" "}
            <Text as="span" fontStyle="italic">
              schnerp
            </Text>{" "}
            a single turn in untracked snow.
          </Text>
          <Text>
            In that context, it vaguely means{" "}
            <Text as="span" fontWeight="semibold">
              to acquire a scarce resource
            </Text>
            . When I was thinking about names for this project, I realized
            we&rsquo;re doing the same here, just with campsites.
          </Text>
          <Text>
            I also wanted something unique for the domain, and low-key wanted
            more people to say the word{" "}
            <Text as="span" fontStyle="italic">
              schnerp
            </Text>
            .
          </Text>
          <Text fontSize={bodyFont} fontWeight="bold">
            It&rsquo;s fun to say. Try it. <SchnerpPronounce color={accent} />
          </Text>
        </Stack>

        <SectionHeading accent={accent}>
          More than two million alerts later
        </SectionHeading>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7">
          <Text>
            What started as one email to my own laptop has now sent{" "}
            <strong>over 2 million</strong>. Right now, someone&rsquo;s phone is
            probably buzzing about a cancellation at a place I&rsquo;ve never
            heard of &mdash; someone I&rsquo;ll never meet, who hit the same
            &ldquo;sold out&rdquo; screen that once stopped me.
          </Text>
          <Text>
            <strong>Parks Canada</strong> and <strong>Recreation.gov</strong>.{" "}
            <strong>DOC</strong> huts on New Zealand&rsquo;s Great Walks.{" "}
            <strong>Park Stay WA</strong> and <strong>Parks Victoria</strong>{" "}
            down in Australia. A long tail of state, provincial, and regional
            systems from Fiordland to Florida.{" "}
            <strong>
              {RESERVATION_SYSTEM_COUNT} booking systems across 4 countries
            </strong>{" "}
            at last count.
          </Text>
          <Text>
            If those alerts have taught me anything, it&rsquo;s that &ldquo;sold
            out&rdquo; doesn&rsquo;t mean forever. Life happens, plans change,
            people cancel &mdash; and the trip you&rsquo;d written off is
            usually still within reach.
          </Text>
          <Text>It&rsquo;s also the only way I get out these days.</Text>
        </Stack>
      </Box>

      {/* Photo grid */}
      <Box maxW="2xl" mx="auto" mt={10} mb={6}>
        <SimpleGrid
          columns={{ base: 1, sm: 2 }}
          spacingX={{ base: 8, md: 10 }}
          spacingY={{ base: 10, md: 14 }}
          py={6}
        >
          {GRID_PHOTOS.map((photo) => (
            <Polaroid key={photo.src} {...photo} />
          ))}
        </SimpleGrid>
      </Box>

      <Box maxW="3xl" mx="auto">
        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7" mt={10}>
          <Text>
            But the best part isn&rsquo;t the places I've been, it's the stories
            I hear from fellow schnerpers.
          </Text>
          <Text>
            Hyeonji wrote to tell me she got engaged at a spot she
            Schnerp&rsquo;d. Anne-Sophie waited three years for Mount
            Assiniboine, then had booking day collide with her licensing exam
            &mdash; one alert later, her crew had the trip of a lifetime at Lake
            Magog. Meg, a grandmother in Calgary, uses it to get spots on the
            water for camping trips with her grandkids. Carlie wrote about
            piecing together the Great Divide Trail with her daughters after a
            rough health year.
          </Text>
          <Text>
            My favourite came from Emma in New Zealand. Twenty-one years ago her
            friend group &mdash; the &ldquo;Heavenly Hikers&rdquo; &mdash;
            walked the Milford Track, minus one friend home with a new baby.
            They spent two decades ticking off every Great Walk while she waited
            for another shot; when they finally got a booking, a cancer
            diagnosis took it away. Then one winter afternoon, an alert landed
            while Emma was doing her online groceries. She phoned her friend,
            fresh out of radiotherapy, and with her on the line booked the walk
            they&rsquo;d been chasing for twenty-one years.
          </Text>
          <Text>
            I keep every one of these emails in a folder called Good Vibes. On
            the hard days, that&rsquo;s where I go.
          </Text>
        </Stack>

        <SectionHeading accent={accent}>
          What Schnerp actually does
        </SectionHeading>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7">
          <Text>
            We watch sold-out campgrounds for you and ping you the second a
            cancellation pops up. That&rsquo;s it. We don&rsquo;t book the site.
            You do that, with a direct link we send. We&rsquo;re not competing
            with the reservation system. We&rsquo;re just the friend at work
            hitting refresh on your behalf, 24/7, with better reflexes.
          </Text>
          <Text>
            The idea hasn&rsquo;t changed since that first script &mdash;
            someone should be watching, even when you&rsquo;ve looked away.
          </Text>
        </Stack>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7" mt={8}>
          <Text>
            In a perfect world there&rsquo;d be enough campsites for everyone
            and nobody would have to refresh a booking page at work. But
            it&rsquo;s not a perfect world, so until then, we keep watching.
            Someone put it better than I can:
          </Text>
        </Stack>

        <PullQuote accent={accent}>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            fontStyle="italic"
            fontWeight="medium"
            color={quoteColor}
            lineHeight="1.45"
            mb={3}
          >
            I kind of hate that Schnerp exists, but I&rsquo;m glad it does.
          </Text>
          <Text fontSize="sm" fontWeight="bold" color={mutedColor}>
            &mdash; Jack Hauen,{" "}
            <Link
              href="https://www.google.com/maps/reviews/data=!4m8!14m7!1m6!2m5!1sChZDSUhNMG9nS0VJQ0FnSUNEeTZpUFlnEAE!2m1!1s0x0:0xf15c3dc980a694eb!3m1!1s2@1:CIHM0ogKEICAgICDy6iPYg%7CCgsIj6qrsAYQoLOHMw%7C?hl=en"
              isExternal
              fontWeight="normal"
              textDecoration="underline"
            >
              Google review
            </Link>
          </Text>
        </PullQuote>

        <Stack spacing={5} fontSize={bodyFont} lineHeight="1.7" mt={6}>
          <Text fontWeight="bold">Yeah Jack, same.</Text>
          <Text>
            Anyway. Go outside. I&rsquo;ll keep an eye on the calendar.
          </Text>
        </Stack>

        <Box mt={6} mb={16}>
          <Text fontSize={bodyFont}>&mdash; Daniel</Text>
        </Box>
      </Box>
    </Container>
  );
}
